/**
 * Artifact 内容的**纯函数**工具（需求 §12.3 / specs/14 §4.5）。
 *
 * 这里只做「确定性变换」，不承载任何业务规则：
 * - 状态合法性、字段是否可采纳、权限，一律由后端校验器（V102/V103）判定；
 * - 本文件不引用任何 query / store，便于单测。
 */

import type { Json } from "@/api/types";

/** 顶层字段的编辑形态：标量走输入框，复杂结构走 JSON 文本域。 */
export type FieldKind = "text" | "number" | "boolean" | "json";

export function isObjectValue(value: Json | undefined): value is { [key: string]: Json } {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** 标量 = 字符串 / 数字 / 布尔 / null；数组与对象都算复杂结构。 */
export function isScalarValue(value: Json | undefined): boolean {
  return value === null || value === undefined || typeof value !== "object";
}

export function fieldKind(value: Json | undefined): FieldKind {
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  if (isScalarValue(value)) return "text";
  return "json";
}

/** 单行摘要：用于差异面板与列表，超长截断。 */
export function formatValue(value: Json | undefined, limit = 120): string {
  if (value === undefined) return "—";
  if (value === null) return "null";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  const text = JSON.stringify(value);
  return text.length > limit ? `${text.slice(0, limit)}…` : text;
}

/** 把编辑框里的文本还原为 JSON 值；`json` 形态解析失败时抛错，由调用方呈现。 */
export function parseFieldDraft(raw: string, kind: FieldKind): Json {
  switch (kind) {
    case "number": {
      const trimmed = raw.trim();
      if (trimmed === "") return null;
      const parsed = Number(trimmed);
      if (Number.isNaN(parsed)) throw new Error(`「${raw}」不是合法数字`);
      return parsed;
    }
    case "boolean": {
      const trimmed = raw.trim().toLowerCase();
      if (trimmed === "true") return true;
      if (trimmed === "false") return false;
      throw new Error(`「${raw}」不是合法布尔值（true / false）`);
    }
    case "json": {
      const trimmed = raw.trim();
      if (trimmed === "") return null;
      try {
        return JSON.parse(trimmed) as Json;
      } catch {
        throw new Error("不是合法 JSON，请检查括号与引号");
      }
    }
    default:
      return raw;
  }
}

/** 编辑框的初始文本：标量原样，复杂结构格式化 JSON（便于人工比对）。 */
export function draftOf(value: Json | undefined): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value, null, 2);
}

function scalarLine(value: Json): string {
  if (value === null) return "null";
  if (typeof value === "string") return value;
  return String(value);
}

function renderObject(value: { [key: string]: Json }, depth: number): string[] {
  const lines: string[] = [];
  for (const key of Object.keys(value)) {
    const child = value[key];
    if (isObjectValue(child)) {
      lines.push(`${"#".repeat(Math.min(depth + 1, 6))} ${key}`, "");
      lines.push(...renderObject(child, depth + 1).filter((line) => line !== ""));
      lines.push("");
      continue;
    }
    if (Array.isArray(child)) {
      lines.push(`- **${key}**：${child.length} 项`);
      for (const item of child) {
        lines.push(isObjectValue(item) ? `  - ${inlineObject(item)}` : `  - ${scalarLine(item)}`);
      }
      continue;
    }
    lines.push(`- **${key}**：${scalarLine(child)}`);
  }
  return lines;
}

function inlineObject(value: { [key: string]: Json }): string {
  return Object.keys(value)
    .map((key) => `${key}=${formatValue(value[key], 40)}`)
    .join(" · ");
}

/**
 * 把结构化内容序列化为 Markdown（**只读投影**，需求 §12.3 的「Markdown 双视图」）。
 *
 * 刻意做成单向序列化：反向解析对嵌套结构是有损的，所以编辑只发生在结构化视图，
 * 不假装 Markdown 视图可以回写。
 */
export function contentToMarkdown(content: Record<string, Json>): string {
  const lines: string[] = [];
  for (const key of Object.keys(content)) {
    const value = content[key];
    lines.push(`## ${key}`, "");
    if (isObjectValue(value)) {
      lines.push(...renderObject(value, 2));
    } else if (Array.isArray(value)) {
      for (const item of value) {
        lines.push(isObjectValue(item) ? `- ${inlineObject(item)}` : `- ${scalarLine(item)}`);
      }
    } else {
      lines.push(scalarLine(value));
    }
    lines.push("");
  }
  return lines.join("\n").trimEnd();
}
